import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/da6umph6oc8j8328n81g';

export default function Contact() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    // Read honeypot field before anything else
    const honeypot = (form.querySelector('[name="contact_alt"]') as HTMLInputElement | null)?.value ?? '';
    if (honeypot.trim() !== '') {
      // Silently treat as success so bots don't get a clear rejection signal
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    const formData = new FormData(form);
    // Remove empty honeypot from payload
    formData.delete('contact_alt');

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      const serverMessage = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = /spam|form data is spam/i.test(serverMessage);

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setFormError('');
        form.reset();
      } else if (isSpam || !response.ok || (parsed && parsed.code && parsed.code !== 'OK')) {
        setStatus('error');
        setFormError(serverMessage || 'Something went wrong. Please try again.');
      } else {
        setStatus('error');
        setFormError(serverMessage || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error. Please check your connection and try again.');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <div className="pt-20 pb-4 bg-white border-b border-stone-100">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
          <nav className="flex items-center gap-2 text-sm text-stone-500">
            <Link to="/" className="hover:text-stone-900 transition-colors cursor-pointer">{t('breadcrumb_home')}</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-stone-900 font-medium">{t('breadcrumb_contact')}</span>
          </nav>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-12 md:py-16">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl border border-stone-200 p-6 md:p-10 shadow-sm">
            <h1 className="text-2xl md:text-3xl font-bold text-stone-900 mb-4">{t('contact_title')}</h1>
            <p className="text-stone-600 leading-relaxed mb-8">{t('contact_desc')}</p>

            <div className="bg-stone-50 rounded-xl p-5 md:p-6 mb-8">
              <h3 className="font-semibold text-stone-900 mb-2">{t('contact_info')}</h3>
              <p className="text-sm text-stone-600">{t('contact_response')}</p>
            </div>

            <form data-readdy-form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-stone-700 mb-1.5">{t('contact_form_name')}</label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-stone-900 outline-none focus:border-stone-400 transition-colors"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-stone-700 mb-1.5">{t('contact_form_email')}</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-stone-900 outline-none focus:border-stone-400 transition-colors"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-stone-700 mb-1.5">{t('contact_form_subject')}</label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-stone-900 outline-none focus:border-stone-400 transition-colors"
                  placeholder="How can we help?"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-stone-700 mb-1.5">{t('contact_form_message')}</label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-stone-900 outline-none focus:border-stone-400 transition-colors resize-none"
                  placeholder="Your message..."
                ></textarea>
              </div>

              {/* Honeypot field - hidden from real users via CSS */}
              <input
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />

              {status === 'success' && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800">
                  Thank you for reaching out! Your message has been sent successfully. We'll get back to you soon.
                </div>
              )}
              {status === 'error' && (
                <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-800">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full px-6 py-3 bg-stone-800 text-white text-sm font-medium rounded-xl hover:bg-stone-700 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? 'Sending...' : t('contact_form_submit')}
              </button>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}